import dayjs from 'dayjs';
import {
  JobOwnerDetailViewType,
  JobOwnerSummaryViewType,
  JobPartnerSummaryViewType,
  PartnerJobDetailType,
  PartnerJobViewType,
} from '../types/services-api';
import { JobPostingCSVJobType, JobPostingCSVPartnerType } from '../types/csv';
import { AuthenticatedDpJobViewType } from '../components/job-search/JobSearchJobDetail';
import { UnauthenticatedDpJobViewType } from '../templates/jobs/job-post-catch-all';
import { JobDetailViewType } from '../components/job-postings/JobDetailLayout';
import {
  excludedSpecialties,
  formatLegacySalary,
  formatSalary,
  jobTypes,
  sanitizeDisplayTitle,
  positionCodeToDisplayName,
  specialtyOptions,
  stateOptions,
  parseStoredPerks,
  benefitsOptions,
  salaryUnitsOptions,
} from './displayHelper';
import { htmlDecode } from './htmlHelper';
import { AlgoliaJobHit } from './algoliaClient';

export function toSalaryNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.replace(/[^0-9.-]/g, '');
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function toStringList(value: unknown): string[] {
  const normalizeItem = (item: string) => item.replace(/^[•\-]\s*/, '').trim();

  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? normalizeItem(item) : ''))
      .filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  // CSV benefits/perks are delimiter-separated with Unit Separator (\u001f).
  if (normalized.includes('\u001f')) {
    return normalized
      .split('\u001f')
      .map((item) => normalizeItem(item))
      .filter(Boolean);
  }

  if (normalized.includes('\n')) {
    return normalized
      .split('\n')
      .map((item) => normalizeItem(item))
      .filter(Boolean);
  }

  return [normalizeItem(normalized)].filter(Boolean);
}

function normalizeJobPhotoUrl(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (/^(https?:)?\/\//i.test(trimmed) || /^data:/i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    const apiBase = process.env.GATSBY_API_URL?.replace(/\/+$/, '') || '';
    if (!apiBase) {
      return trimmed;
    }

    const normalizedPath = trimmed.startsWith('/api9/')
      ? trimmed
      : `/api9${trimmed}`;

    return `${apiBase}${normalizedPath}`;
  }

  return trimmed;
}

export function isJobDetailViewType(job: unknown): job is JobDetailViewType {
  return (
    !!job &&
    typeof job === 'object' &&
    'postedLabel' in job &&
    'compensation' in job &&
    'photoUrl' in job &&
    'jobUrl' in job
  );
}

export function transformJobDetailParams(
  job:
    | AuthenticatedDpJobViewType
    | JobPostingCSVJobType
    | UnauthenticatedDpJobViewType
    | PartnerJobDetailType
    | JobPostingCSVPartnerType
    | PartnerJobViewType
    | JobOwnerDetailViewType
    | JobOwnerSummaryViewType
    | JobPartnerSummaryViewType
    | AlgoliaJobHit
): JobDetailViewType {
  if (isJobDetailViewType(job)) {
    return job;
  }

  const minSalary = 'minSalary' in job ? toSalaryNumber(job.minSalary) : null;
  const maxSalary = 'maxSalary' in job ? toSalaryNumber(job.maxSalary) : null;
  const hasSalaryUnit =
    'salaryUnits' in job &&
    typeof job.salaryUnits === 'string' &&
    job.salaryUnits.length > 0;

  const normalizedJobDates =
    'jobDates' in job
      ? typeof job.jobDates === 'string'
        ? job.jobDates
            .split(',')
            .map((date) => date.trim())
            .filter(Boolean)
        : Array.isArray(job.jobDates)
          ? job.jobDates
              .map((day) => {
                if (typeof day === 'string') return day;
                if (day && typeof day === 'object' && 'date' in day) {
                  return String(day.date).trim();
                }
                return '';
              })
              .filter(Boolean)
          : []
      : [];

  const positionLabel =
    job.position in positionCodeToDisplayName
      ? positionCodeToDisplayName[job.position]
      : job.position;

  const specialtyLabel =
    'specialty' in job &&
    !!job.specialty &&
    !excludedSpecialties.includes(job.specialty)
      ? specialtyOptions[job.specialty] || ''
      : '';

  const rawTitle =
    'customTitle' in job
      ? job.customTitle ||
        [specialtyLabel, positionLabel].filter(Boolean).join(' ')
      : 'title' in job
        ? htmlDecode(job.title)
        : positionLabel;

  const postedDate =
    ('postedAt' in job && typeof job.postedAt === 'string' && job.postedAt) ||
    ('updatedDate' in job &&
      typeof job.updatedDate === 'string' &&
      job.updatedDate) ||
    ('createdDate' in job &&
      typeof job.createdDate === 'string' &&
      job.createdDate) ||
    '';
  const parsedPostedDate = postedDate ? dayjs(postedDate) : null;
  const daysAgo = parsedPostedDate?.isValid()
    ? dayjs().startOf('day').diff(parsedPostedDate.startOf('day'), 'days')
    : 0;
  const postedLabel =
    daysAgo <= 0
      ? 'Posted today'
      : `Posted ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
  return {
    aboutThePractice: 'aboutThePractice' in job ? job.aboutThePractice : null,
    aboutTheRole: 'aboutTheRole' in job ? job.aboutTheRole : null,
    applyType:
      'jobKey' in job
        ? 'Partner'
        : 'applylink' in job && !!job.applylink
          ? 'ExternalATS'
          : 'Internal',
    appliedDate: 'appliedDate' in job ? job.appliedDate : null,
    applylink: 'applylink' in job ? job.applylink : '',
    benefits:
      'benefits' in job
        ? toStringList(job.benefits).map(
            (benefit) => benefitsOptions[benefit]?.label || benefit
          )
        : [],
    city: job.city,
    compensation:
      Number(minSalary) > 0 && hasSalaryUnit
        ? formatSalary({
            minSalary,
            maxSalary,
            salaryUnits: job.salaryUnits,
          })
        : 'salaryUnits' in job && salaryUnitsOptions.includes(job.salaryUnits) //appcast jobs have DP schema but salary units can be USD or non-structructed text, so we check if the salary unit is in our options before formatting
          ? formatLegacySalary(job.salary, job.salaryUnits).trim()
          : (job.salary ?? 'Not Provided'),
    createdDate:
      'postedAt' in job ? null : dayjs(job.updatedDate).format('MMM DD, YYYY'),
    description: 'description' in job ? job.description : '',
    distance: 'distance' in job ? job.distance : null,
    dow: 'dow' in job ? job.dow : null,
    employerId:
      'advertiserId' in job
        ? job.advertiserId
        : 'employer' in job
          ? job.employer
          : 'Not Provided',
    employerName:
      'company' in job
        ? job.company
        : 'employerName' in job
          ? job.employerName
          : (job.name ?? 'Confidential Employer'),
    employerWebsite:
      'employerWebsite' in job
        ? job.employerWebsite
        : 'partnerUrl' in job
          ? job.partnerUrl
          : '',
    expireDate:
      'expireDate' in job && dayjs(job.expireDate).isBefore(dayjs())
        ? job.expireDate
        : null,
    invitedDate: 'invitedDate' in job ? job.invitedDate : null,
    inviteCredits: 'inviteCredits' in job ? job.inviteCredits : null,
    inviteCreditsIncluded:
      'inviteCreditsIncluded' in job ? job.inviteCreditsIncluded : null,
    inviteDebits: 'inviteDebits' in job ? job.inviteDebits : null,
    isBasic: 'isBasic' in job ? job.isBasic : false,
    isConfidential:
      'isConfidential' in job
        ? typeof job.isConfidential === 'boolean'
          ? job.isConfidential
          : job.isConfidential === 'true'
        : false,
    isDislike: 'isDislike' in job ? job.isDislike : false,
    isDistro:
      'isDistro' in job
        ? typeof job.isDistro === 'boolean'
          ? job.isDistro
          : job.isDistro === 'true'
        : false,
    isExpired:
      'isExpired' in job
        ? job.isExpired
        : 'expireDate' in job && !!job.expireDate
          ? dayjs(job.expireDate).isBefore(dayjs())
          : false,
    isLike: 'isLike' in job ? job.isLike : false,
    isNational:
      'isNational' in job
        ? typeof job.isNational === 'boolean'
          ? job.isNational
          : job.isNational === 'true'
        : false,
    isSubscription: 'isSubscription' in job ? job.isSubscription : false,
    isUnslotted: 'isUnslotted' in job ? job.isUnslotted : false,
    isViewed:
      'viewedDate' in job
        ? !!job.viewedDate
        : 'isViewed' in job && typeof job.isViewed === 'boolean'
          ? job.isViewed
          : 'isViewed' in job && typeof job.isViewed === 'string'
            ? job.isViewed === 'true'
            : false,
    jobDates: normalizedJobDates,
    // `externalId` wins where the API sends one. An externally hosted job (Appcast) arrives in
    // premiumJobs looking like one of ours, but its numeric `id` is a hash the API derives at
    // response time and stores nowhere, so asking for the job back by that id always 404'd.
    // `externalId` is the job's real key, which the detail endpoint can resolve. Setting it here
    // rather than at each call site means links, de-duplication, self-exclusion and sort keys all
    // agree on one id.
    jobId:
      'externalId' in job &&
      typeof job.externalId === 'string' &&
      !!job.externalId
        ? job.externalId
        : 'jobId' in job
          ? job.jobId.toString()
          : 'jobKey' in job
            ? job.jobKey
            : job.id.toString(),
    jobType:
      'jobType' in job
        ? jobTypes[job.jobType as keyof typeof jobTypes] || job.jobType
        : '',
    jobUrl: 'uri' in job ? job.uri : '',
    lat: 'lat' in job ? Number(job.lat) : 0,
    lng: 'lng' in job ? Number(job.lng) : 0,
    location: `${job.city}, ${job.state} ${job.zip}`,
    matchId: 'matchId' in job ? job.matchId : null,
    partnerUrl: 'partnerUrl' in job ? job.partnerUrl : '',
    perks:
      'additionalPerks' in job ? parseStoredPerks(job.additionalPerks) : [],
    photoUrl:
      'photoUrl' in job
        ? normalizeJobPhotoUrl(job.photoUrl)
        : 'companyLogo' in job
          ? normalizeJobPhotoUrl(job.companyLogo)
          : 'photoThumbnailUrl' in job
            ? normalizeJobPhotoUrl(job.photoThumbnailUrl)
            : '',
    position: positionLabel,
    postedDate: postedDate,
    postedLabel: postedLabel,
    signOnBonusAmount:
      'signOnBonusAmount' in job ? Number(job.signOnBonusAmount) : null,
    source: 'source' in job ? job.source : null,
    specialty: 'specialty' in job ? job.specialty : '',
    state: job.state in stateOptions ? stateOptions[job.state] : job.state,
    status: 'status' in job ? job.status : null,
    title: sanitizeDisplayTitle(rawTitle) || positionLabel,
    urgentlyHiring:
      'urgentlyHiring' in job
        ? typeof job.urgentlyHiring === 'boolean'
          ? job.urgentlyHiring
          : job.urgentlyHiring === 'true'
        : false,
    viewedDate: 'viewedDate' in job ? job.viewedDate : null,
    whatYoullDo: 'whatYoullDo' in job ? job.whatYoullDo : null,
    whatYoullPreferred:
      'whatYoullPreferred' in job && job.whatYoullPreferred
        ? toStringList(job.whatYoullPreferred)
        : null,
    whatYoullRequired:
      'whatYoullRequired' in job && job.whatYoullRequired
        ? toStringList(job.whatYoullRequired)
        : null,
    zip: job.zip,
  };
}
